import React from 'react';
import {
  ArrowDownTrayIcon,
  FolderArrowDownIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/**
 * Permissions subset required by BulkActionBar.
 */
interface BulkActionBarPermissions {
  download: boolean;
  delete: boolean;
}

/**
 * Props for the BulkActionBar component.
 */
interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  isDownloading: boolean;
  permissions: BulkActionBarPermissions;
  onDownload: () => void;
  onMove: () => void;
  onDelete: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onCancelDownload: () => void;
  /** True when the current selection contains at least one GFR/GDA (Hard-Archive) file. */
  hasHardArchiveSelected?: boolean;
  /** True when the current selection contains GIR (Soft-Archive) files but no Hard-Archive files. */
  hasSoftArchiveSelected?: boolean;
}

/**
 * Toolbar shown when files are selected or a batch download is in progress.
 * Contains selection controls and bulk action buttons.
 * All business logic is delegated to the parent via callbacks.
 */
const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  totalCount,
  isDownloading,
  permissions,
  onDownload,
  onMove,
  onDelete,
  onSelectAll,
  onClearSelection,
  onCancelDownload,
  hasHardArchiveSelected = false,
  hasSoftArchiveSelected = false,
}) => {
  // Render nothing when there's no selection and no active download
  if (selectedCount === 0 && !isDownloading) return null;

  return (
    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
      {isDownloading ? (
        // Download in progress view
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-blue-700 font-medium">
            Download in progress — check notifications for status
          </p>
          <button
            onClick={onCancelDownload}
            aria-label="Cancel download"
            className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
          >
            <XMarkIcon className="h-4 w-4" />
            <span>Cancel Download</span>
          </button>
        </div>
      ) : (
        // Selection controls view
        <div>
          {/* Hard-Archive freeze banner */}
          {hasHardArchiveSelected && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg p-3 text-sm mb-3">
              Selection contains Glacier archive files (GFR/GDA). All operations are disabled. Remove archive files from your selection to proceed.
            </div>
          )}

          {/* Soft-Archive (GIR) info banner — only shown when no Hard-Archive present */}
          {!hasHardArchiveSelected && hasSoftArchiveSelected && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3 text-sm mb-3">
              Selection contains Glacier Instant Retrieval files. Move is not permitted.
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-blue-700">
                {selectedCount} of {totalCount} selected
              </span>
              <button
                onClick={onSelectAll}
                aria-label="Select all items"
                className="text-xs text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                Select All
              </button>
              <button
                onClick={onClearSelection}
                aria-label="Clear selection"
                className="text-xs text-gray-500 hover:text-gray-700 underline focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
              >
                Clear
              </button>
            </div>

            <div className="flex items-center space-x-2">
              {permissions.download && (
                <button
                  onClick={hasHardArchiveSelected ? undefined : onDownload}
                  disabled={hasHardArchiveSelected}
                  aria-label="Download selected files"
                  className={`inline-flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500${hasHardArchiveSelected ? ' opacity-50 cursor-not-allowed' : ''}`}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download</span>
                </button>
              )}

              {permissions.download && (
                <button
                  onClick={(hasHardArchiveSelected || hasSoftArchiveSelected) ? undefined : onMove}
                  disabled={hasHardArchiveSelected || hasSoftArchiveSelected}
                  aria-label="Move selected files"
                  className={`inline-flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400${(hasHardArchiveSelected || hasSoftArchiveSelected) ? ' opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FolderArrowDownIcon className="h-4 w-4" />
                  <span>Move</span>
                </button>
              )}

              {permissions.delete && (
                <button
                  onClick={hasHardArchiveSelected ? undefined : onDelete}
                  disabled={hasHardArchiveSelected}
                  aria-label="Delete selected files"
                  className={`inline-flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500${hasHardArchiveSelected ? ' opacity-50 cursor-not-allowed' : ''}`}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionBar;
